import { useQuery } from '@apollo/client';

import { GET_PRICES_LIST } from '~/graphql/prices';
import { PricingPlansType } from '~/pages/PricePage/types';
import { Pricing, Query, QueryListPricingsArgs } from '~/types';
import { checkIsSSR } from '~/utils/checkIsSSR';

function isIndividualPricing(item: Pricing) {
  const { enterprise, chargebee_plan_info } = item;

  if (Boolean(enterprise)) {
    return false;
  }

  return (
    (!chargebee_plan_info?.max_team_members ?? !chargebee_plan_info?.min_team_members) ||
    (chargebee_plan_info?.max_team_members === 1 && chargebee_plan_info?.min_team_members === 1)
  );
}

interface PricingPlanFilters {
  planType: PricingPlansType;
}

function filterPricingPlans({ planType }: PricingPlanFilters) {
  return (item: Pricing) => {
    const isIndividual = isIndividualPricing(item);

    // add other checks here when needed
    const matchesType = planType === 'individual' ? isIndividual : !isIndividual;

    return matchesType;
  };
}

const usePlans = (filterBy: PricingPlanFilters) => {
  const isSSR = checkIsSSR();

  const { data, loading } = useQuery<Pick<Query, 'listPricings'>, QueryListPricingsArgs>(
    GET_PRICES_LIST,
    { skip: isSSR },
  );

  const isLoading = loading || isSSR;

  if (!data) {
    return { isLoading, availableCurrencies: [], availableDurations: [], plans: [] };
  }

  const plans = data.listPricings
    .filter(filterPricingPlans(filterBy))
    .sort((a, b) => a.enterprise - b.enterprise);

  const availableCurrencies = plans.reduce((acc, item) => {
    const { chargebee_plan_info } = item;
    if (chargebee_plan_info?.currency_code) {
      acc.add(chargebee_plan_info.currency_code);
    }

    return acc;
  }, new Set<string>());

  const availableDurations = plans.reduce((acc, item) => {
    const { pricing_duration } = item;
    if (pricing_duration) {
      acc.add(pricing_duration.id);
    }

    return acc;
  }, new Set<string>());

  return {
    isLoading,
    plans,
    availableCurrencies: Array.from(availableCurrencies),
    availableDurations: Array.from(availableDurations),
  };
};

export default usePlans;
